import { Story } from '../types/Story';
import { StoryConfig, GenerationResponse } from '../types/StoryConfig';
import { ApiResponse } from '../types/ApiResponse';
import { get, post, put, patch, del } from './api';

// Get all stories (paginated)
export const getStories = async (page = 1, limit = 10, filter?: string, sortBy?: string): Promise<ApiResponse<{ stories: Story[]; total: number }>> => {
  return await get<{ stories: Story[]; total: number }>('/stories', { page, limit, filter, sortBy });
};

// Get story by ID
export const getStoryById = async (id: string): Promise<ApiResponse<Story>> => {
  return await get<Story>(`/stories/${id}`);
};

// Get user's stories
export const getUserStories = async (userId: string): Promise<ApiResponse<Story[]>> => {
  return await get<Story[]>(`/users/${userId}/stories`);
};

/*
 * Generation persists the story itself now, so this is NOT part of the
 * generate-then-save flow — calling it there would create a duplicate.
 * Kept for any caller that genuinely needs to create a story outright.
 */
export const createStory = async (storyData: Partial<Story>): Promise<ApiResponse<Story>> => {
  return await post<Story>('/stories', storyData);
};

/*
 * Promotes an auto-saved (private) story to public. Stories start private,
 * so the private direction needs no call at all.
 */
export const updateStoryVisibility = async (
  id: string,
  userId: string,
  visibility: 'public' | 'private'
): Promise<ApiResponse<Story>> => {
  return await patch<Story>(`/stories/${id}/visibility`, { userId, visibility });
};

// Update a story
export const updateStory = async (id: string, storyData: Partial<Story>): Promise<ApiResponse<Story>> => {
  return await put<Story>(`/stories/${id}`, storyData);
};

// Delete a story
export const deleteStory = async (id: string): Promise<ApiResponse<void>> => {
  return await del<void>(`/stories/${id}`);
};

export const generateStory = async (config: StoryConfig, userId: string): Promise<ApiResponse<GenerationResponse>> => {
  return await post<GenerationResponse>('/stories/generate', { 
    config, 
    userId 
  });
};

// Generate cover image for story
export const generateCoverImage = async (prompt: string): Promise<ApiResponse<{ imageUrl: string }>> => {
  return await post<{ imageUrl: string }>('/stories/generate-image', { prompt });
};

// Like a story
export const likeStory = async (id: string): Promise<ApiResponse<{ likes: number }>> => {
  return await post<{ likes: number }>(`/stories/${id}/like`, {});
};

export const unlikeStory = async (id: string): Promise<ApiResponse<{ likes: number }>> => {
  return await post<{ likes: number }>(`/stories/${id}/unlike`, {});
};

export const incrementReadCount = async (id: string): Promise<ApiResponse<{ readCount: number }>> => {
  return await post<{ readCount: number }>(`/stories/${id}/read`, {});
};

export const toggleLikeStory = async (storyId: string, userId: string): Promise<ApiResponse<{ likes: number; liked: boolean }>> => {
  return await post(`/stories/${storyId}/like`, { userId });
};